import React, { useState, useRef, useEffect, useCallback } from 'react'
import './ModTooltip.css'

/**
 * Tooltip component that wraps an activator element
 * @param {Object} props
 * @param {React.ReactNode} props.children - Activator element (wrapped by tooltip)
 * @param {string|React.ReactNode} props.content - Tooltip content
 * @param {string} [props.className] - Additional CSS classes
 * @param {'top'|'bottom'|'left'|'right'|'cursor'} [props.position='cursor'] - Tooltip position
 * @param {number} [props.delay=150] - Delay before showing tooltip (ms)
 * @param {number} [props.hideDelay=300] - Delay before hiding tooltip (ms) - allows moving to tooltip
 */
export default function ModTooltip({
  children,
  content,
  className = '',
  position = 'cursor',
  delay = 150,
  hideDelay = 300,
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const wrapperRef = useRef(null)
  const tooltipRef = useRef(null)
  const showTimeoutRef = useRef(null)
  const hideTimeoutRef = useRef(null)
  const isHoveringTooltipRef = useRef(false)
  const isHoveringWrapperRef = useRef(false)
  const fixedPositionRef = useRef(null) // Fixed position - doesn't change until fully hidden

  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(
    e => {
      isHoveringWrapperRef.current = true
      clearTimeouts()

      // Only capture position if tooltip is not visible and no fixed position yet
      if (position === 'cursor' && !fixedPositionRef.current) {
        fixedPositionRef.current = { x: e.clientX, y: e.clientY }
      }

      showTimeoutRef.current = setTimeout(() => {
        setIsVisible(true)
      }, delay)
    },
    [delay, clearTimeouts, position]
  )

  const handleMouseLeave = useCallback(() => {
    isHoveringWrapperRef.current = false
    clearTimeouts()

    hideTimeoutRef.current = setTimeout(() => {
      // Don't hide if hovering over tooltip itself
      if (!isHoveringTooltipRef.current) {
        setIsVisible(false)
        fixedPositionRef.current = null // Reset position only when fully hidden
      }
    }, hideDelay)
  }, [hideDelay, clearTimeouts])

  const handleTooltipMouseEnter = useCallback(() => {
    isHoveringTooltipRef.current = true
    clearTimeouts()
  }, [clearTimeouts])

  const handleTooltipMouseLeave = useCallback(() => {
    isHoveringTooltipRef.current = false
    clearTimeouts()

    hideTimeoutRef.current = setTimeout(() => {
      // Don't hide if back on wrapper
      if (!isHoveringWrapperRef.current) {
        setIsVisible(false)
        fixedPositionRef.current = null // Reset position only when fully hidden
      }
    }, hideDelay)
  }, [hideDelay, clearTimeouts])

  // Position tooltip based on fixed cursor position or element
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const style = {}

      if (position === 'cursor' && fixedPositionRef.current) {
        // Position near the fixed cursor position
        const offsetX = 12
        const offsetY = -tooltipRect.height - 8

        style.left = fixedPositionRef.current.x + offsetX
        style.top = fixedPositionRef.current.y + offsetY

        // If tooltip goes above screen, show below cursor
        if (style.top < 8) {
          style.top = fixedPositionRef.current.y + 20
        }
      } else if (wrapperRef.current) {
        const wrapperRect = wrapperRef.current.getBoundingClientRect()

        switch (position) {
          case 'top':
            style.left = wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2
            style.top = wrapperRect.top - tooltipRect.height - 8
            break
          case 'bottom':
            style.left = wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2
            style.top = wrapperRect.bottom + 8
            break
          case 'left':
            style.left = wrapperRect.left - tooltipRect.width - 8
            style.top = wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2
            break
          case 'right':
            style.left = wrapperRect.right + 8
            style.top = wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2
            break
        }
      }

      // Keep within screen bounds
      if (style.left < 8) style.left = 8
      if (style.left + tooltipRect.width > window.innerWidth - 8) {
        style.left = window.innerWidth - tooltipRect.width - 8
      }
      if (style.top < 8) style.top = 8
      if (style.top + tooltipRect.height > window.innerHeight - 8) {
        style.top = window.innerHeight - tooltipRect.height - 8
      }

      setTooltipStyle(style)
    }
  }, [isVisible, position])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts()
    }
  }, [clearTimeouts])

  if (!content) {
    return <>{children}</>
  }

  return (
    <div
      ref={wrapperRef}
      className={`mod-tooltip-wrapper ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`mod-tooltip mod-tooltip--${position === 'cursor' ? 'top' : position}`}
          style={tooltipStyle}
          role='tooltip'
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {content}
        </div>
      )}
    </div>
  )
}
